import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#00D9FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const MetricCard = ({ label, value, unit, color = 'text-primary', icon }) => (
  <div className="card p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon && <Icon name={icon} size={14} className="text-muted-foreground" />}
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
    <p className={`text-2xl font-mono font-bold ${color}`}>
      {value ?? '—'}{unit && <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span>}
    </p>
  </div>
);

const PerformanceDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [slowQueries, setSlowQueries] = useState([]);
  const [latencyHistory, setLatencyHistory] = useState([]);
  const [operationBreakdown, setOperationBreakdown] = useState([]);
  const [dbHealth, setDbHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get overall metrics
      const { data: metricsData } = await supabase?.rpc('get_performance_metrics', { p_hours: 24 });
      setMetrics(metricsData);

      // Get slowest queries
      const { data: slowData } = await supabase?.from('performance_logs')
        ?.select('query_name, operation_type, duration_ms, error_message, created_at')
        ?.order('duration_ms', { ascending: false })
        ?.limit(10);
      setSlowQueries(slowData || []);

      // Get latency over time (last 24h, grouped by hour)
      const { data: historyData } = await supabase?.from('performance_logs')
        ?.select('created_at, duration_ms, operation_type')
        ?.gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString())
        ?.order('created_at', { ascending: true })
        ?.limit(200);

      // Group by hour
      if (historyData?.length > 0) {
        const hourMap = {};
        historyData?.forEach(row => {
          const hour = new Date(row.created_at)?.getHours();
          const key = `${hour}:00`;
          if (!hourMap?.[key]) hourMap[key] = { time: key, avg: 0, count: 0, total: 0 };
          hourMap[key].total += row?.duration_ms || 0;
          hourMap[key].count += 1;
          hourMap[key].avg = Math.round(hourMap?.[key]?.total / hourMap?.[key]?.count);
        });
        setLatencyHistory(Object.values(hourMap)?.slice(-12));
      }

      // Operation breakdown
      if (historyData?.length > 0) {
        const opMap = {};
        historyData?.forEach(row => {
          const op = row?.operation_type || 'SELECT';
          opMap[op] = (opMap?.[op] || 0) + 1;
        });
        setOperationBreakdown(Object.entries(opMap)?.map(([name, value]) => ({ name, value })));
      }

      // DB health
      const { data: healthData } = await supabase?.rpc('check_database_health');
      setDbHealth(healthData);

      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.message || 'Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const errorRateColor = (rate) => {
    if (rate < 1) return 'text-success';
    if (rate < 5) return 'text-warning';
    return 'text-red-400';
  };

  const latencyColor = (ms) => {
    if (ms < 200) return 'text-success';
    if (ms < 500) return 'text-warning';
    return 'text-red-400';
  };

  if (loading && !metrics) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0,1,2,3]?.map(i => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-3 w-24 rounded bg-primary/10 mb-2" />
              <div className="h-8 w-16 rounded bg-primary/10" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="Activity" size={20} className="text-primary" />
            Performance Dashboard
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {lastRefresh ? `Last updated: ${lastRefresh?.toLocaleTimeString()}` : 'Loading...'} · Auto-refresh 30s
          </p>
        </div>
        <button
          onClick={fetchMetrics}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 border border-border transition-all min-h-[36px] disabled:opacity-50"
        >
          <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>
      {error && (
        <div className="card p-4 border-red-500/30">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <Icon name="AlertCircle" size={14} />{error}
          </p>
        </div>
      )}
      {/* DB Health */}
      {dbHealth && (
        <div className={`card p-4 border ${
          dbHealth?.status === 'healthy' ? 'border-success/30 bg-success/5' : 'border-red-500/30 bg-red-500/5'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              dbHealth?.status === 'healthy' ? 'bg-success animate-pulse' : 'bg-red-400'
            }`} />
            <div>
              <p className={`text-sm font-semibold ${
                dbHealth?.status === 'healthy' ? 'text-success' : 'text-red-400'
              }`}>
                Database {dbHealth?.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
              </p>
              <p className="text-xs text-muted-foreground">
                Latency: {dbHealth?.latency_ms}ms · {dbHealth?.stocks_count} stocks · {dbHealth?.users_count} users · {dbHealth?.factions_count} factions
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Avg Latency"
          value={metrics?.avg_latency_ms ?? '—'}
          unit="ms"
          color={latencyColor(metrics?.avg_latency_ms || 0)}
          icon="Zap"
        />
        <MetricCard
          label="P95 Latency"
          value={metrics?.p95_latency_ms ?? '—'}
          unit="ms"
          color={latencyColor(metrics?.p95_latency_ms || 0)}
          icon="TrendingUp"
        />
        <MetricCard
          label="P99 Latency"
          value={metrics?.p99_latency_ms ?? '—'}
          unit="ms"
          color={latencyColor(metrics?.p99_latency_ms || 0)}
          icon="AlertTriangle"
        />
        <MetricCard
          label="Error Rate"
          value={metrics?.error_rate_pct ?? '—'}
          unit="%"
          color={errorRateColor(metrics?.error_rate_pct || 0)}
          icon="XCircle"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard label="Total Queries (24h)" value={metrics?.total_queries ?? 0} unit="" icon="Database" />
        <MetricCard label="Errors (24h)" value={metrics?.error_count ?? 0} unit="" color="text-red-400" icon="AlertCircle" />
        <MetricCard
          label="DB Connection"
          value={dbHealth?.latency_ms ? `${dbHealth?.latency_ms}ms` : '—'}
          unit=""
          color={dbHealth?.latency_ms < 100 ? 'text-success' : 'text-warning'}
          icon="Wifi"
        />
      </div>
      {/* Latency Chart */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">API Latency (Last 24h)</h3>
        {latencyHistory?.length > 1 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={latencyHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} tickFormatter={v => `${v}ms`} />
              <Tooltip
                contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px' }}
                formatter={(v) => [`${v}ms`, 'Avg Latency']}
              />
              <Line type="monotone" dataKey="avg" stroke="var(--color-primary)" strokeWidth={2} dot={false} name="Avg Latency" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-48 flex items-center justify-center">
            <p className="text-muted-foreground text-sm">No latency data yet. Queries will appear here as they are logged.</p>
          </div>
        )}
      </div>
      {/* Operation Breakdown + Slow Queries */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Operation Pie Chart */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Operation Breakdown</h3>
          {operationBreakdown?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={operationBreakdown} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}>
                  {operationBreakdown?.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <p className="text-muted-foreground text-sm">No operation data yet</p>
            </div>
          )}
        </div>

        {/* Slowest Queries */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Slowest Queries</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {slowQueries?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No slow queries logged yet</p>
            ) : (
              slowQueries?.map((q, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/30">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground truncate">{q?.query_name}</p>
                    <p className="text-xs text-muted-foreground">{q?.operation_type}</p>
                  </div>
                  <div className="text-right ml-2">
                    <p className={`text-xs font-mono font-bold ${
                      q?.duration_ms > 1000 ? 'text-red-400' : q?.duration_ms > 500 ? 'text-warning' : 'text-success'
                    }`}>{q?.duration_ms?.toFixed(0)}ms</p>
                    {q?.error_message && <p className="text-xs text-red-400">Error</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Performance Alerts */}
      {(metrics?.avg_latency_ms > 1000 || metrics?.error_rate_pct > 5) && (
        <div className="card p-4 border-red-500/30 bg-red-500/5">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-red-400" />
            <h3 className="text-sm font-semibold text-red-400">Performance Alerts</h3>
          </div>
          <div className="space-y-1">
            {metrics?.avg_latency_ms > 1000 && (
              <p className="text-xs text-red-400">⚠ Average query latency exceeds 1000ms ({metrics?.avg_latency_ms}ms)</p>
            )}
            {metrics?.error_rate_pct > 5 && (
              <p className="text-xs text-red-400">⚠ Error rate exceeds 5% ({metrics?.error_rate_pct}%)</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceDashboard;
